import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  Button,
  Flex,
  Skeleton,
  ChatMessageWidget,
  EWidgetType,
  type IChatMessageActionCard,
  Icon,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
  Badge,
  Spinner,
} from '@kognitos/lattice';
import {
  useRetrieveWorkspaceBook,
  useRetrieveLatestWorkspaceBooks,
  useRetrieveAllWorkspaceBookConnections,
} from '@/generated/hooks/bdk/v2/services/library';
import type {
  Bdkv2Value,
  V2BookConnectionDescriptor,
} from '@/generated/hooks/bdk/models';
import {
  hasManagedOAuthReturn,
  useManagedOAuthReturn,
  type IOAuthConnectionResume,
} from '@/modules/credentials';
import {
  AddConnectionDialog,
  SelectConnectionDialog,
  isConnectionReady,
  useConnectionActions,
  useOAuthCallbackError,
  useWaitForConnectionToBeReady,
  type IFilteredConnection,
} from '@/modules/integrations';
import type { IChatMessageStructureConnection } from '../../chat/interface';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { convertToDataUrl } from '@/shared/utils/image-utils';
import { useBoolean } from 'usehooks-ts';
import { FilterBuilder, validateOAuthRedirectUrl } from '@/shared/utils';
import { isNotFoundError } from '@/shared/utils/errors';
import { captureException } from '@/modules/sentry';
import { hasPendingTriggerConnection } from '@/modules/triggers/utils/trigger-connection-intent';

/**
 * Props for the chat integration connection widget.
 */
interface IChatIntegrationConnectionProps {
  integration_name: string;
  /**
   * Version of the integration (book) to bind to. Optional: an agent asking for
   * a connection rarely knows a book's real semver (sap's latest is 2.6.2), so
   * when it is absent — or names a version that doesn't exist — the widget
   * resolves the latest version by name and offers every connection for the
   * app. When it does name a real version, only that version's connections are
   * offered.
   */
  integration_version?: string;
  onSelectConnection?: (connection: IChatMessageStructureConnection) => void;
  connectionId?: string;
  /**
   * Why the connection is being requested, in the requester's words (the
   * `request_connection` tool's `reason`). Rendered as the card subtitle, in
   * place of the generic add/select hint.
   */
  reason?: string;
  /**
   * Optional per-connection label shown as the card subtitle so that multiple
   * connections of the same integration (e.g. two Slack connections) are
   * distinguishable. When present it replaces the generic "add/select"
   * description; when absent the description falls back to the default hint.
   * This is display-only passthrough data — it does not affect binding.
   */
  connectionLabel?: string;
  /**
   * The request this card served is over and produced no connection — the user
   * skipped it, or the call failed. Renders read-only: the integration and its
   * reason stay legible as history, but there is nothing left to act on (posting
   * a result for a resolved tool call is a no-op, so a live button would lie).
   * The badge states the outcome rather than the cause, since the two are
   * indistinguishable here and a skip is already evident from the user's own
   * message in the transcript.
   */
  isDeclined?: boolean;
  /**
   * Called once when no integration of this name exists in the workspace — the
   * by-name lookup succeeded and came back empty (`books:latest` answers an
   * unknown name with `200 []`, never a 404). Deliberately not fired when that
   * lookup *fails*: an errored request says nothing about the app. The widget
   * renders a notice naming what it couldn't find; the caller decides how to
   * recover (Rain reports a tool failure to the agent).
   */
  onUnavailable?: () => void;
  /**
   * Stable id of the request this card answers (e.g. its chat message id), so
   * a sign-in started from this card reopens here and not on a sibling card
   * for the same integration.
   */
  requestId?: string;
}

/** Tags the card's sign-ins, so the automation menu on the same page doesn't reopen them. */
const CHAT_OAUTH_ORIGIN = 'chat';

/**
 * Chat widget for selecting or adding a connection to an integration (book).
 *
 * @param props - Component props
 * @param props.integration_name - Integration (book) name
 * @param props.integration_version - Integration (book) version
 * @param props.onSelectConnection - Callback when a connection is selected
 * @param props.connectionId - Optional pre-selected connection ID
 * @returns Chat integration connection widget
 */
export function ChatIntegrationConnection(
  props: IChatIntegrationConnectionProps,
) {
  const {
    integration_name,
    integration_version,
    onSelectConnection,
    connectionId,
    connectionLabel,
    reason,
    isDeclined,
    onUnavailable,
    requestId,
  } = props;
  const oauthOrigin = requestId
    ? `${CHAT_OAUTH_ORIGIN}:${requestId}`
    : CHAT_OAUTH_ORIGIN;
  const {
    value: isAddConnectionDialogOpen,
    setTrue: openAddConnectionDialog,
    setFalse: closeAddConnectionDialog,
  } = useBoolean(false);
  const [isWaitingForConnection, setIsWaitingForConnection] = useState(false);
  const [selectedConnection, setSelectedConnection] =
    useState<IChatMessageStructureConnection | null>(null);
  const hasSelectedConnectionRef = useRef(false);
  const hasReportedUnavailableRef = useRef(false);
  const {
    value: isSelectConnectionDialogOpen,
    setTrue: openSelectConnectionDialog,
    setFalse: closeSelectConnectionDialog,
  } = useBoolean(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const oauthConnectionIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const showOAuthCallbackError = useOAuthCallbackError();

  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { t: tCommon } = useTranslation('common');
  const { t: tIntegrations } = useTranslation('integrations');

  const hasRequestedVersion = !!integration_version;

  const {
    data: integrationData,
    isLoading: isExactVersionLoading,
    isFetching: isExactVersionFetching,
    error: integrationError,
    refetch: refetchExactVersion,
  } = useRetrieveWorkspaceBook(
    organizationId,
    workspaceId,
    integration_name,
    integration_version ?? '',
    {
      query: {
        enabled:
          !!organizationId &&
          !!workspaceId &&
          !!integration_name &&
          hasRequestedVersion,
      },
    },
  );
  const isRequestedVersionMissing = isNotFoundError(integrationError);

  // Resolve the book by name whenever there's no usable version: either none was
  // requested, or the requested one doesn't exist (agents guess "2" where the
  // book is 2.6.2). The `books:latest` filter matches names case-insensitively
  // and returns the same descriptor as the exact lookup, so it can drive the
  // connection dialogs identically.
  const isNameLookupEnabled =
    !!organizationId &&
    !!workspaceId &&
    !!integration_name &&
    (!hasRequestedVersion || isRequestedVersionMissing);
  const {
    data: latestBooksData,
    isLoading: isLatestVersionLoading,
    isFetching: isLatestVersionFetching,
    error: latestBooksError,
    refetch: refetchLatestBooks,
  } = useRetrieveLatestWorkspaceBooks(
    organizationId,
    workspaceId,
    {
      page_size: 1,
      filter: FilterBuilder.create().equals('name', integration_name).build(),
    },
    { query: { enabled: isNameLookupEnabled } },
  );

  const integration =
    integrationData?.data.book ?? latestBooksData?.data?.books?.[0];
  const isIntegrationLoading = isExactVersionLoading || isLatestVersionLoading;

  // Back from an OAuth provider: reopen the wizard where it left off.
  const [oauthResume, setOAuthResume] = useState<IOAuthConnectionResume | null>(
    null,
  );
  const handleOAuthResume = useCallback(
    (resume: IOAuthConnectionResume) => {
      setOAuthResume(resume);
      openAddConnectionDialog();
    },
    [openAddConnectionDialog],
  );
  useManagedOAuthReturn({
    // A card already answered (or closed) didn't start the sign-in.
    integrationName:
      connectionId || isDeclined ? '' : (integration?.name ?? ''),
    origin: oauthOrigin,
    onResume: handleOAuthResume,
  });

  /**
   * The version to narrow connections by — set only when the agent asked for a
   * version that actually exists. Otherwise every connection for the app is a
   * valid answer, so no version clause is applied anywhere.
   */
  const connectionVersion =
    hasRequestedVersion && !isRequestedVersionMissing
      ? integration_version
      : undefined;

  // No book carries this name at all. This requires the lookup to have actually
  // succeeded: `books:latest` answers an unknown name with `200 []`, never a
  // 404, so an *absent* response means the request failed — not that the app is
  // missing. Concluding "unavailable" from a 5xx would post an irreversible tool
  // failure to the agent (the report below is one-shot) over a transient blip.
  const isIntegrationNotFound =
    isNameLookupEnabled &&
    !isLatestVersionLoading &&
    latestBooksData !== undefined &&
    !latestBooksData.data?.books?.length;

  /**
   * The lookup itself failed (after React Query's retries), so we know nothing
   * about this app. Distinct from "not found": the agent is told nothing, and
   * the user gets a retry instead of a shimmer that never resolves.
   */
  const hasExactVersionFailed = Boolean(
    integrationError && !isRequestedVersionMissing,
  );
  const hasLookupFailed = Boolean(latestBooksError) || hasExactVersionFailed;
  const isRetryingLookup = isExactVersionFetching || isLatestVersionFetching;

  /**
   * Retry whichever lookup actually broke. With a version supplied and its
   * lookup failing on something other than a 404, the by-name query is disabled
   * — refetching only that one would leave `integrationError` set and the notice
   * stuck behind a button that can't clear it.
   */
  const retryLookup = () => {
    if (hasExactVersionFailed) void refetchExactVersion();
    if (latestBooksError) void refetchLatestBooks();
  };

  // The agent can request a connection for an integration that doesn't exist.
  // Report it once to the caller (Rain forwards a tool failure to the agent)
  // instead of showing a perpetual loading skeleton.
  // eslint-disable-next-line no-restricted-syntax -- reason: fire one-shot side effect when the by-name lookup resolves to no such book
  useEffect(() => {
    if (!isIntegrationNotFound || hasReportedUnavailableRef.current) return;
    hasReportedUnavailableRef.current = true;
    onUnavailable?.();
  }, [isIntegrationNotFound, onUnavailable]);

  const connectionsFilter = useMemo(() => {
    const builder = FilterBuilder.create().has('book_name', integration_name);
    if (connectionVersion) {
      builder.has('book_version', connectionVersion);
    }
    return builder.build();
  }, [integration_name, connectionVersion]);

  const { data: connectionsData } = useRetrieveAllWorkspaceBookConnections(
    organizationId,
    workspaceId,
    { page_size: 100, filter: connectionsFilter },
  );

  const groupedConnections = useMemo(() => {
    const allConnections = connectionsData?.data?.book_connections || [];

    // Re-filter client-side, mirroring the server filter. The book name is
    // compared case-insensitively because the requester may use a display-style
    // name ("SAP") where the book is named "sap".
    const filtered = allConnections.filter(
      conn =>
        isConnectionReady(conn) &&
        conn.book_name?.toLowerCase() === integration_name.toLowerCase() &&
        (!connectionVersion || conn.book_version === connectionVersion),
    );

    // Group by connection_name
    const grouped = new Map<string, V2BookConnectionDescriptor[]>();
    filtered.forEach(conn => {
      const name = conn.connection_name || conn.connection_id || '';
      if (!grouped.has(name)) {
        grouped.set(name, []);
      }
      grouped.get(name)!.push(conn);
    });

    return grouped;
  }, [connectionsData, integration_name, connectionVersion]);

  const hasConnections = groupedConnections.size > 0;

  // Use the hook for connection readiness polling
  const waitForConnectionToBeReady = useWaitForConnectionToBeReady();

  // Cleanup: abort any pending polling when component unmounts
  // -- reason: Cleanup AbortController on unmount to prevent memory leaks
  // eslint-disable-next-line no-restricted-syntax
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  /**
   * Waits for a connection to be ready and auto-selects it.
   * Only processes if the connection belongs to this component's integration.
   *
   * @param connId - Connection ID to wait for
   * @param connName - Connection name (fallback)
   */
  const handleWaitForConnection = useCallback(
    async (connId: string, connName: string): Promise<void> => {
      if (hasSelectedConnectionRef.current) return;

      // Abort any previous polling request
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setIsWaitingForConnection(true);
      hasSelectedConnectionRef.current = false;

      try {
        const result = await waitForConnectionToBeReady(
          connId,
          abortControllerRef.current.signal,
        );

        // Don't update state if cancelled
        if (result.isCancelled) return;

        // Verify the connection belongs to this component's integration
        // This ensures only the correct ChatIntegrationConnection handles OAuth
        // returns. Name matching is case-insensitive, and the version is only
        // checked when the request narrowed to one (see connectionVersion).
        if (
          result.connection?.book_name?.toLowerCase() !==
            integration_name.toLowerCase() ||
          (connectionVersion &&
            result.connection?.book_version !== connectionVersion)
        ) {
          return;
        }

        if (result.isReady && !hasSelectedConnectionRef.current) {
          hasSelectedConnectionRef.current = true;
          const chatConnection: IChatMessageStructureConnection = {
            connection_id: connId,
            connection_name:
              result.connection?.connection_name || connName || '',
          };
          setSelectedConnection(chatConnection);
          onSelectConnection?.(chatConnection);
        }
      } finally {
        setIsWaitingForConnection(false);
      }
    },
    [
      waitForConnectionToBeReady,
      onSelectConnection,
      integration_name,
      connectionVersion,
    ],
  );

  // Handle OAuth return - check for data param with connection_id
  // eslint-disable-next-line no-restricted-syntax -- reason: Handle OAuth callback URL param on mount/URL change
  useEffect(() => {
    const oauthConnectionId = searchParams.get('data');
    const connectionError = searchParams.get('error');

    // A trigger dialog on this page owns this OAuth return; the connection was
    // created for its trigger, not for this chat request.
    if (hasPendingTriggerConnection(pathname)) {
      return;
    }
    // A managed sign-in's return: ?data= is a credential's id, not a connection's.
    if (hasManagedOAuthReturn(searchParams)) {
      return;
    }

    if (connectionError) {
      showOAuthCallbackError(connectionError, 'ChatIntegrationConnection');
    }

    // Clear the OAuth callback params from the URL immediately so the toast
    // (and the connection wait) fire once rather than on every remount — e.g.
    // when the import checklist is collapsed/re-expanded. The `data` param is
    // skipped if it was already processed for this id, but `error` is always
    // cleared so a stale failure doesn't re-toast.
    const isUnprocessedData =
      oauthConnectionId && oauthConnectionIdRef.current !== oauthConnectionId;
    if (connectionError || isUnprocessedData) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('error');
      if (isUnprocessedData) {
        nextParams.delete('data');
      }
      setSearchParams(nextParams, { replace: true });
    }

    // Skip if no connection_id in URL or already processed
    if (!isUnprocessedData) {
      return;
    }

    // Mark this connection_id as being processed
    oauthConnectionIdRef.current = oauthConnectionId;

    // Wait for the OAuth connection to be ready using the hook
    handleWaitForConnection(oauthConnectionId, '');
  }, [
    searchParams,
    setSearchParams,
    handleWaitForConnection,
    showOAuthCallbackError,
    pathname,
  ]);

  const {
    createConnection,
    updateConnection,
    createOAuthAuthorizationConnection,
  } = useConnectionActions(
    organizationId,
    workspaceId,
    // Always create/update against the resolved book: the requester's name may
    // differ in case, and its version may not exist at all.
    integration?.name ?? integration_name,
    integration?.version ?? integration_version ?? '',
  );

  // Handle OAuth click - redirect to OAuth provider
  const handleOAuthClick = useCallback(
    async (
      authMethodId: string,
      connectionName: string,
      credentials: Record<string, string>,
      labels: string[],
      configMap?: Record<string, Bdkv2Value>,
    ) => {
      try {
        const authorizeUrl = await createOAuthAuthorizationConnection(
          integration || {},
          authMethodId,
          connectionName,
          credentials,
          labels,
          configMap,
        );

        // Validate URL before redirecting to prevent open redirect vulnerabilities
        validateOAuthRedirectUrl(authorizeUrl);

        // Redirect to OAuth authorization URL
        window.location.assign(authorizeUrl);
      } catch (error) {
        captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: {
              component: 'ChatIntegrationConnection',
              action: 'createOAuthAuthorizationConnection',
            },
          },
        );
        throw error; // Re-throw to let dialog handle loading state
      }
    },
    [integration, createOAuthAuthorizationConnection],
  );

  /**
   * Returns the postfix or action props for the connection action card
   * based on the current connection state.
   *
   * @param isConnectionSelected - Whether a connection is already selected
   * @returns Partial action card props with either a postfix badge or action button
   */
  const getConnectionActionCardProps = (): Partial<IChatMessageActionCard> => {
    // A skipped/cancelled request keeps its row as history with a terminal
    // badge. Checked after the selected-connection branch below only for the
    // no-connection case: if a connection was picked, "Connected" is the truer
    // end state.
    if (isDeclined && !selectedConnection && !isWaitingForConnection) {
      return {
        // Same muted X marker RainAttachFileCard uses for a cancelled picker,
        // so a closed card reads the same wherever it appears.
        postfix: (
          <Badge variant="secondary">
            <Icon type="X" size="sm" className="text-muted-foreground" />
            {tIntegrations('integrations.connectionItem.status.noConnection')}
          </Badge>
        ),
      };
    }
    if (selectedConnection || isWaitingForConnection) {
      return {
        postfix: isWaitingForConnection ? (
          <Spinner size="sm" variant="muted" />
        ) : (
          <Badge variant="secondary">
            <Icon type="CircleCheck" size="sm" className="text-success" />
            {tIntegrations('integrations.connectionItem.status.connected')}
          </Badge>
        ),
      };
    }

    // Case where connectionId is provided, but no connection with that ID is found
    // We show nothing in this case. This might happen when the connection is deleted
    // Or removed from the workspace
    if (connectionId) {
      return {};
    }

    return {
      actionProps: {
        label: hasConnections
          ? tCommon('common.action.select')
          : tCommon('common.action.connect'),
        onClick: () =>
          hasConnections
            ? openSelectConnectionDialog()
            : openAddConnectionDialog(),
        isLoading: isWaitingForConnection,
        variant: 'outline',
        size: 'sm',
      },
    };
  };

  const renderAction = () => {
    const actionCards: IChatMessageActionCard[] = [
      {
        id: 'connect',
        title: (integration?.display_name || '') + ' v' + integration?.version,
        // Prefer the per-connection label (e.g. "Dave's connection") as the
        // subtitle so same-integration rows are distinguishable, then the
        // requester's reason, which says more than the generic hint it replaces.
        // Both are superseded once the user actually picks a connection, at
        // which point the selected connection name is more useful.
        description: selectedConnection
          ? selectedConnection.connection_name
          : connectionLabel ||
            reason ||
            (connectionId || isWaitingForConnection
              ? undefined
              : hasConnections
                ? tIntegrations('integrations.connections.selectOrAddNew')
                : tIntegrations('integrations.connections.addNew')),
        fallbackIcon: (
          <Flex align="start" justify="center">
            <img
              src={convertToDataUrl(integration?.icon)}
              alt={integration?.display_name}
            />
          </Flex>
        ),
        ...getConnectionActionCardProps(),
      },
    ];

    return (
      <ChatMessageWidget
        widget={{
          type: EWidgetType.ACTION_CARDS,
          actionCards,
        }}
      />
    );
  };

  /** A read-only row in the card's shape, for the states with nothing to act on. */
  const renderNotice = (icon: ReactNode, title: string, action?: ReactNode) => (
    <Item variant="outline" size="sm" className="w-full bg-card">
      <ItemMedia variant="image" className="w-5">
        {icon}
      </ItemMedia>
      <ItemContent>
        <ItemTitle>{title}</ItemTitle>
        {reason ? <ItemDescription>{reason}</ItemDescription> : null}
      </ItemContent>
      {action ? <ItemActions>{action}</ItemActions> : null}
    </Item>
  );

  const renderContent = () => {
    // The requested app doesn't exist. `onUnavailable` tells the agent; this
    // says so to the user too, who would otherwise watch the request vanish
    // from the transcript with no explanation.
    if (isIntegrationNotFound) {
      return renderNotice(
        <Icon type="AlertCircle" size="sm" className="text-muted-foreground" />,
        tIntegrations('integrations.connections.appNotFound', {
          app: integration_name,
        }),
      );
    }
    // The lookup failed rather than answering. Nothing is claimed to the agent;
    // the user can retry, which is the only thing that can actually help here.
    if (hasLookupFailed && !integration) {
      return renderNotice(
        <Icon type="AlertCircle" size="sm" className="text-warning" />,
        tIntegrations('integrations.connections.lookupFailed'),
        <Button
          variant="outline"
          size="sm"
          onClick={retryLookup}
          isLoading={isRetryingLookup}
          disabled={isRetryingLookup}
        >
          {tCommon('common.action.retry')}
        </Button>,
      );
    }
    if (isIntegrationLoading || !integration) {
      // Mirror the loaded action card (lattice `Item size="sm"` from
      // ChatActionCardWidget) so the skeleton reserves the exact same box —
      // icon, two text lines, and a trailing action button. This keeps the
      // shimmer-to-content swap in place and avoids the per-row height jump
      // that caused cascading layout shift across the checklist's connections.
      return (
        <Item variant="outline" size="sm" className="w-full bg-card">
          <ItemMedia variant="image" className="w-5">
            <Skeleton className="size-6 bg-alternate-1" />
          </ItemMedia>
          <ItemContent>
            <Skeleton className="h-5 w-40 bg-alternate-1" />
            <Skeleton className="h-5 w-24 bg-alternate-1" />
          </ItemContent>
          <ItemActions>
            <Skeleton className="h-8 w-20 bg-alternate-1" />
          </ItemActions>
        </Item>
      );
    }
    return renderAction();
  };

  // eslint-disable-next-line no-restricted-syntax -- reason: Set selected connection when connectionId is provided
  useEffect(() => {
    if (connectionId) {
      const connection = connectionsData?.data?.book_connections?.find(
        conn => conn.connection_id === connectionId,
      );
      if (connection) {
        setSelectedConnection({
          connection_id: connection.connection_id || '',
          connection_name: connection.connection_name || '',
        });
      }
    }
  }, [connectionId, connectionsData]);

  return (
    <>
      {renderContent()}

      {integration ? (
        <AddConnectionDialog
          key={oauthResume ? 'oauth-resume' : 'add'}
          isOpen={isAddConnectionDialogOpen}
          integration={integration!}
          createConnection={createConnection}
          updateConnection={updateConnection}
          onOAuthClick={handleOAuthClick}
          supportsOAuthResume
          oauthOrigin={oauthOrigin}
          oauthResume={oauthResume}
          onSuccess={(draftConnection, _publishedConnection) => {
            setOAuthResume(null);
            closeAddConnectionDialog();

            // Use the returned draft connection directly if available
            if (draftConnection?.connection_id) {
              handleWaitForConnection(
                draftConnection.connection_id,
                draftConnection.connection_name || '',
              );
            }
          }}
          onCancel={() => {
            setOAuthResume(null);
            closeAddConnectionDialog();
          }}
        />
      ) : null}

      <SelectConnectionDialog
        isOpen={isSelectConnectionDialogOpen}
        onClose={closeSelectConnectionDialog}
        integration={integration}
        connections={Array.from(groupedConnections.values()).flat()}
        onSelectConnection={(connection: IFilteredConnection) => {
          const chatConnection: IChatMessageStructureConnection = {
            connection_id: connection.connection_id,
            connection_name: connection.connection_name,
          };
          setSelectedConnection(chatConnection);
          onSelectConnection?.(chatConnection);
          closeSelectConnectionDialog();
        }}
        onAddConnection={() => {
          openAddConnectionDialog();
          closeSelectConnectionDialog();
        }}
      />
    </>
  );
}
